import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ICE_COLORS } from '../CyanMaterial';

// Frost wave that expands outward from impact point
const FrostWaveRing = ({ x, y, delay }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.35) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 0.35;
    const easeOut = 1 - Math.pow(1 - progress, 3);

    // Expand outward
    const scale = 0.2 + easeOut * 1.8;
    meshRef.current.scale.set(scale, scale * 0.3, 1);
    meshRef.current.material.opacity = 0.6 * (1 - progress);
  });

  return (
    <mesh ref={meshRef} position={[x, y, 0.15]} rotation={[0, 0, 0]} visible={false}>
      <ringGeometry args={[0.3, 0.5, 16]} />
      <meshBasicMaterial
        color={ICE_COLORS.frost}
        transparent
        opacity={0.6}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

// Ice crack lines spreading from impact
const IceCrackLine = ({ startX, startY, angle, length, delay }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.25) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 0.25;

    // Crack extends outward then fades
    const extend = Math.min(progress * 3, 1);
    const fade = progress > 0.5 ? 1 - (progress - 0.5) * 2 : 1;

    meshRef.current.scale.x = extend;
    meshRef.current.material.opacity = 0.8 * fade;
  });

  const endX = startX + Math.cos(angle) * length * 0.5;
  const endY = startY + Math.sin(angle) * length * 0.5;
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <mesh
      ref={meshRef}
      position={[midX, midY, 0.12]}
      rotation={[0, 0, angle]}
      visible={false}
    >
      <planeGeometry args={[length, 0.03]} />
      <meshBasicMaterial
        color={ICE_COLORS.crystal}
        transparent
        opacity={0.8}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// Frozen sparkle particle
const FrozenSparkle = ({ position, delay }) => {
  const meshRef = useRef();
  const startTime = useRef(null);
  const rotationSpeed = useMemo(() => (Math.random() - 0.5) * 10, []);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.3) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 0.3;

    // Quick flash and fade
    const intensity = Math.sin(progress * Math.PI);
    meshRef.current.scale.setScalar(0.08 * intensity);
    meshRef.current.rotation.z += rotationSpeed * 0.016;
    meshRef.current.material.opacity = intensity;
  });

  return (
    <mesh ref={meshRef} position={position} visible={false}>
      <octahedronGeometry args={[0.08, 0]} />
      <meshBasicMaterial
        color={ICE_COLORS.snow}
        transparent
        opacity={1}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// Vertical freeze flash along drop path
const FreezeFlash = ({ x, startY, endY }) => {
  const meshRef = useRef();
  const startTime = useRef(null);
  const trailHeight = Math.abs(startY - endY);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    if (elapsed > 0.15) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = elapsed / 0.15;

    // Flash then fade
    const flash = progress < 0.3 ? progress / 0.3 : 1 - (progress - 0.3) / 0.7;
    meshRef.current.material.opacity = 0.5 * flash;

    // Slight pulse
    meshRef.current.scale.x = 0.4 + flash * 0.2;
  });

  return (
    <mesh ref={meshRef} position={[x, endY + trailHeight / 2, 0.05]} visible={false}>
      <planeGeometry args={[0.4, trailHeight]} />
      <meshBasicMaterial
        color={ICE_COLORS.frost}
        transparent
        opacity={0.5}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// Impact frost burst at landing point
const ImpactFrostBurst = ({ x, y }) => {
  const groupRef = useRef();
  const particles = useRef([]);
  const startTime = useRef(null);

  const particleCount = 6;

  useFrame((state) => {
    if (!groupRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;

    if (elapsed > 0.25) {
      groupRef.current.visible = false;
      return;
    }

    groupRef.current.visible = true;

    const progress = elapsed / 0.25;
    const easeOut = 1 - Math.pow(1 - progress, 2);

    particles.current.forEach((particle, i) => {
      if (!particle) return;
      const angle = (i / particleCount) * Math.PI * 2;
      const distance = easeOut * 0.6;

      particle.position.x = Math.cos(angle) * distance;
      particle.position.y = Math.sin(angle) * distance * 0.5 + easeOut * 0.3;
      particle.scale.setScalar(0.08 * (1 - progress));
      particle.material.opacity = 0.7 * (1 - progress);
    });
  });

  return (
    <group ref={groupRef} position={[x, y, 0.2]} visible={false}>
      {Array.from({ length: particleCount }).map((_, i) => (
        <mesh
          key={i}
          ref={(el) => particles.current[i] = el}
        >
          <circleGeometry args={[0.08, 6]} />
          <meshBasicMaterial
            color={ICE_COLORS.snow}
            transparent
            opacity={0.7}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

const IceHardDropTrail = ({ startY, endY, columns, onComplete }) => {
  const startTime = useRef(Date.now());

  // Generate frost wave rings at impact points
  const frostWaves = useMemo(() => {
    return columns.map((col, i) => ({
      x: col - 5 + 0.5,
      y: endY + 0.5,
      delay: i * 0.02
    }));
  }, [columns, endY]);

  // Generate ice crack lines from each impact point
  const iceCracks = useMemo(() => {
    const cracks = [];
    columns.forEach((col, colIndex) => {
      const x = col - 5 + 0.5;
      const y = endY + 0.5;

      // 4-6 crack lines per column radiating outward
      const crackCount = 4 + Math.floor(Math.random() * 3);
      for (let i = 0; i < crackCount; i++) {
        const baseAngle = (i / crackCount) * Math.PI * 2;
        const angle = baseAngle + (Math.random() - 0.5) * 0.5;
        const length = 0.3 + Math.random() * 0.4;

        cracks.push({
          startX: x,
          startY: y,
          angle,
          length,
          delay: colIndex * 0.015 + Math.random() * 0.03
        });
      }
    });
    return cracks;
  }, [columns, endY]);

  // Generate frozen sparkles along the path
  const sparkles = useMemo(() => {
    const data = [];
    columns.forEach((col) => {
      const x = col - 5 + 0.5;
      const sparkleCount = 3;

      for (let i = 0; i < sparkleCount; i++) {
        const t = (i + 0.5) / sparkleCount;
        const y = endY + (startY - endY) * t;

        data.push({
          position: [
            x + (Math.random() - 0.5) * 0.3,
            y + (Math.random() - 0.5) * 0.3,
            0.18
          ],
          delay: i * 0.025
        });
      }
    });
    return data;
  }, [columns, startY, endY]);

  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;

    if (elapsed > 0.4) {
      onComplete?.();
    }
  });

  return (
    <group>
      {/* Quick freeze flash along drop path */}
      {columns.map((col, i) => {
        const x = col - 5 + 0.5;
        return (
          <FreezeFlash
            key={`flash-${i}`}
            x={x}
            startY={startY}
            endY={endY}
          />
        );
      })}

      {/* Frost wave rings at impact */}
      {frostWaves.map((wave, i) => (
        <FrostWaveRing
          key={`wave-${i}`}
          x={wave.x}
          y={wave.y}
          delay={wave.delay}
        />
      ))}

      {/* Ice crack lines spreading from impact */}
      {iceCracks.map((crack, i) => (
        <IceCrackLine
          key={`crack-${i}`}
          startX={crack.startX}
          startY={crack.startY}
          angle={crack.angle}
          length={crack.length}
          delay={crack.delay}
        />
      ))}

      {/* Frozen sparkles */}
      {sparkles.map((sparkle, i) => (
        <FrozenSparkle
          key={`sparkle-${i}`}
          position={sparkle.position}
          delay={sparkle.delay}
        />
      ))}

      {/* Impact frost bursts */}
      {columns.map((col, i) => (
        <ImpactFrostBurst
          key={`burst-${i}`}
          x={col - 5 + 0.5}
          y={endY + 0.5}
        />
      ))}
    </group>
  );
};

export default IceHardDropTrail;
